/**
 * TimePickerInput component - Hour and minute selection for notification preferences
 * 
 * Provides separate dropdowns for hour (0-23) and minute (0, 15, 30, 45) selection
 * with clear labels and proper accessibility.
 */
import React, { useId } from 'react';

interface TimePickerInputProps {
  hour: number;
  minute: number;
  onChange: (hour: number, minute: number) => void;
  disabled?: boolean;
  label?: string;
}

export const TimePickerInput: React.FC<TimePickerInputProps> = ({
  hour,
  minute,
  onChange,
  disabled = false,
  label = 'Preferred Time',
}) => {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = [0, 15, 30, 45];

  const formatHour = (h: number): string => {
    if (h === 0) return '12 AM';
    if (h < 12) return `${h} AM`;
    if (h === 12) return '12 PM';
    return `${h - 12} PM`;
  };

  const handleHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(parseInt(e.target.value, 10), minute);
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(hour, parseInt(e.target.value, 10));
  };

  // The heading labels the hour/minute pair, not a single control, so it is a group label rather
  // than a <label> — which would have pointed at nothing. Each select keeps its own aria-label.
  const groupLabelId = useId();

  return (
    <div role="group" aria-labelledby={groupLabelId}>
      <span
        id={groupLabelId}
        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
      >
        {label}
      </span>
      <div className="flex gap-3">
        <div className="flex-1">
          <select
            value={hour}
            onChange={handleHourChange}
            disabled={disabled}
            className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:bg-gray-100 disabled:dark:bg-gray-900 disabled:cursor-not-allowed"
            aria-label="Hour"
          >
            {hours.map((h) => (
              <option key={h} value={h}>
                {formatHour(h)}
              </option>
            ))}
          </select>
          <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">Hour</span>
        </div>
        
        <div className="flex items-center text-2xl font-bold text-gray-400 dark:text-gray-600 pb-6">
          :
        </div>
        
        <div className="flex-1">
          <select
            value={minute}
            onChange={handleMinuteChange}
            disabled={disabled}
            className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:bg-gray-100 disabled:dark:bg-gray-900 disabled:cursor-not-allowed"
            aria-label="Minute"
          >
            {minutes.map((m) => (
              <option key={m} value={m}>
                {m.toString().padStart(2, '0')}
              </option>
            ))}
          </select>
          <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">Minute</span>
        </div>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
        Notifications will be sent at this time in your selected timezone
      </p>
    </div>
  );
};

export default TimePickerInput;
